import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { contactService } from '../../../services/contactService';
import { useAuth } from '../../../contexts/AuthContext';

const ContactDrawer = ({ contact, isOpen, onClose, onSave }) => {
  const { profile, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [newNote, setNewNote] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [formData, setFormData] = useState({
    // Universal fields
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    secondaryPhone: '',
    contactType: 'customer',
    status: 'lead',
    assignedUserId: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    // Customer/Personal fields
    householdType: '',
    homeownerStatus: '',
    spousePartnerName: '',
    squareFootage: '',
    yearBuilt: '',
    gateCode: '',
    accessInstructions: '',
    hasPets: false,
    petNotes: '',
    // Business fields
    businessName: '',
    industry: '',
    mainPhone: '',
    website: '',
    primaryContactId: '',
    secondaryContactIds: [],
    // Other
    jobTitle: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadDropdownData();
    }
  }, [isOpen]);

  const loadDropdownData = async () => {
    try {
      const superAdmin = isSuperAdmin();
      const tenantId = superAdmin ? null : profile?.tenantId;
      const [usersData, contactsData] = await Promise.all([
        contactService?.getUsersForDropdown(tenantId),
        contactService?.getCustomerContactsForDropdown(tenantId, superAdmin)
      ]);
      setUsers(usersData);
      setCustomerContacts(contactsData);
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    }
  };

  useEffect(() => {
    if (contact) {
      setFormData({
        firstName: contact?.firstName || '',
        lastName: contact?.lastName || '',
        email: contact?.email || '',
        phone: contact?.phone || '',
        secondaryPhone: contact?.secondaryPhone || '',
        contactType: contact?.contactType || 'customer',
        status: contact?.status || 'lead',
        assignedUserId: contact?.assignedUserId || '',
        streetAddress: contact?.streetAddress || '',
        city: contact?.city || '',
        state: contact?.state || '',
        zipCode: contact?.zipCode || '',
        householdType: contact?.householdType || '',
        homeownerStatus: contact?.homeownerStatus || '',
        spousePartnerName: contact?.spousePartnerName || '',
        squareFootage: contact?.squareFootage || '',
        yearBuilt: contact?.yearBuilt || '',
        gateCode: contact?.gateCode || '',
        accessInstructions: contact?.accessInstructions || '',
        hasPets: contact?.hasPets || false,
        petNotes: contact?.petNotes || '',
        businessName: contact?.businessName || '',
        industry: contact?.industry || '',
        mainPhone: contact?.mainPhone || '',
        website: contact?.website || '',
        primaryContactId: contact?.primaryContactId || '',
        secondaryContactIds: contact?.secondaryContactIds || [],
        jobTitle: contact?.jobTitle || '',
        notes: contact?.notes || ''
      });
      setIsEditing(false);
    } else {
      // New contact mode
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        secondaryPhone: '',
        contactType: 'customer',
        status: 'lead',
        assignedUserId: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        householdType: '',
        homeownerStatus: '',
        spousePartnerName: '',
        squareFootage: '',
        yearBuilt: '',
        gateCode: '',
        accessInstructions: '',
        hasPets: false,
        petNotes: '',
        businessName: '',
        industry: '',
        mainPhone: '',
        website: '',
        primaryContactId: '',
        secondaryContactIds: [],
        jobTitle: '',
        notes: ''
      });
      setIsEditing(true);
    }
  }, [contact, isOpen]);

  const handleSave = () => {
    // All fields are optional per requirements
    onSave?.(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (contact) {
      setFormData({
        firstName: contact?.firstName || '',
        lastName: contact?.lastName || '',
        email: contact?.email || '',
        phone: contact?.phone || '',
        secondaryPhone: contact?.secondaryPhone || '',
        contactType: contact?.contactType || 'customer',
        status: contact?.status || 'lead',
        assignedUserId: contact?.assignedUserId || '',
        streetAddress: contact?.streetAddress || '',
        city: contact?.city || '',
        state: contact?.state || '',
        zipCode: contact?.zipCode || '',
        householdType: contact?.householdType || '',
        homeownerStatus: contact?.homeownerStatus || '',
        spousePartnerName: contact?.spousePartnerName || '',
        squareFootage: contact?.squareFootage || '',
        yearBuilt: contact?.yearBuilt || '',
        gateCode: contact?.gateCode || '',
        accessInstructions: contact?.accessInstructions || '',
        hasPets: contact?.hasPets || false,
        petNotes: contact?.petNotes || '',
        businessName: contact?.businessName || '',
        industry: contact?.industry || '',
        mainPhone: contact?.mainPhone || '',
        website: contact?.website || '',
        primaryContactId: contact?.primaryContactId || '',
        secondaryContactIds: contact?.secondaryContactIds || [],
        jobTitle: contact?.jobTitle || '',
        notes: contact?.notes || ''
      });
      setIsEditing(false);
    } else {
      onClose();
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'User' },
    { id: 'deals', label: 'Deals', icon: 'Target' },
    { id: 'activities', label: 'Activities', icon: 'Clock' },
    { id: 'notes', label: 'Notes', icon: 'FileText' }
  ];

  const isBusinessContact = formData?.contactType === 'business';
  const isCustomerContact = formData?.contactType === 'customer';

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        />
      )}
      {/* Drawer */}
      <div
        className={`
          fixed top-0 right-0 h-full w-full sm:w-96 lg:w-[600px] bg-background border-l border-border z-50
          transform transition-transform duration-300 ease-out overflow-y-auto
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-background z-10">
            <h2 className="text-lg font-semibold text-foreground">
              {contact ? 'Contact Details' : 'Add New Contact'}
            </h2>
            <div className="flex items-center gap-2">
              {isEditing && (
                <>
                  <Button variant="outline" size="sm" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave}>
                    <Icon name="Save" size={16} className="mr-2" />
                    Save
                  </Button>
                </>
              )}
              {!isEditing && contact && (
                <Button size="sm" onClick={() => setIsEditing(true)}>
                  <Icon name="Edit" size={16} className="mr-2" />
                  Edit
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                aria-label="Close contact details"
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-border sticky top-[73px] bg-background z-10">
            <nav className="flex space-x-1 px-6">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex items-center space-x-2 px-3 py-3 text-sm font-medium border-b-2 transition-colors
                    ${activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Contact Type */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2.5">
                    Contact Type
                  </label>
                  {isEditing ? (
                    <select
                      value={formData?.contactType}
                      onChange={(e) => setFormData({ ...formData, contactType: e?.target?.value })}
                      className="w-full h-11 px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 focus:shadow-xl focus:shadow-gray-400/50 bg-background text-foreground font-medium transition-all duration-200 hover:border-gray-400"
                    >
                      <option value="customer">Customer</option>
                      <option value="business">Business</option>
                      <option value="vendor">Vendor</option>
                      <option value="partner">Partner</option>
                      <option value="lead">Lead</option>
                    </select>
                  ) : (
                    <p className="text-base text-foreground capitalize">{formData?.contactType}</p>
                  )}
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2.5">
                    Status
                  </label>
                  {isEditing ? (
                    <select
                      value={formData?.status}
                      onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
                      className="w-full h-11 px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 focus:shadow-xl focus:shadow-gray-400/50 bg-background text-foreground font-medium transition-all duration-200 hover:border-gray-400"
                    >
                      <option value="lead">Lead</option>
                      <option value="prospect">Prospect</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="past_customer">Past Customer</option>
                    </select>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize">
                      {formData?.status?.replace('_', ' ')}
                    </span>
                  )}
                </div>

                {/* Assigned User */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2.5">
                    Assigned User / Owner
                  </label>
                  {isEditing ? (
                    <select
                      value={formData?.assignedUserId}
                      onChange={(e) => setFormData({ ...formData, assignedUserId: e?.target?.value })}
                      className="w-full h-11 px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 focus:shadow-xl focus:shadow-gray-400/50 bg-background text-foreground font-medium transition-all duration-200 hover:border-gray-400"
                    >
                      <option value="">Select user...</option>
                      {users?.map((user) => (
                        <option key={user?.id} value={user?.id}>
                          {user?.fullName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-base text-foreground">
                      {contact?.assignedUser ? `${contact?.assignedUser?.first_name} ${contact?.assignedUser?.last_name}` : 'Not assigned'}
                    </p>
                  )}
                </div>

                <div className="border-t border-border pt-6">
                  <h3 className="text-md font-semibold text-foreground mb-5">Primary Contact Information</h3>
                  
                  {/* First Name & Last Name */}
                  <div className="grid grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2.5">
                        First Name
                      </label>
                      {isEditing ? (
                        <Input
                          value={formData?.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e?.target?.value })}
                          placeholder="Enter first name"
                        />
                      ) : (
                        <p className="text-base text-foreground">{formData?.firstName || 'N/A'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2.5">
                        Last Name
                      </label>
                      {isEditing ? (
                        <Input
                          value={formData?.lastName}
                          onChange={(e) => setFormData({ ...formData, lastName: e?.target?.value })}
                          placeholder="Enter last name"
                        />
                      ) : (
                        <p className="text-base text-foreground">{formData?.lastName || 'N/A'}</p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-foreground mb-2.5">
                      Email
                    </label>
                    {isEditing ? (
                      <Input
                        type="email"
                        value={formData?.email}
                        onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                        placeholder="Enter email"
                      />
                    ) : (
                      <p className="text-base text-foreground">{formData?.email || 'N/A'}</p>
                    )}
                  </div>

                  {/* Mobile Phone */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-foreground mb-2.5">
                      Mobile Phone
                    </label>
                    {isEditing ? (
                      <Input
                        value={formData?.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                        placeholder="Enter mobile phone"
                      />
                    ) : (
                      <p className="text-base text-foreground">{formData?.phone || 'N/A'}</p>
                    )}
                  </div>

                  {/* Secondary Phone */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-foreground mb-2.5">
                      Secondary Phone
                    </label>
                    {isEditing ? (
                      <Input
                        value={formData?.secondaryPhone}
                        onChange={(e) => setFormData({ ...formData, secondaryPhone: e?.target?.value })}
                        placeholder="Enter secondary phone"
                      />
                    ) : (
                      <p className="text-base text-foreground">{formData?.secondaryPhone || 'N/A'}</p>
                    )}
                  </div>

                  {/* Street Address */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-foreground mb-2.5">
                      Street Address
                    </label>
                    {isEditing ? (
                      <Input
                        value={formData?.streetAddress}
                        onChange={(e) => setFormData({ ...formData, streetAddress: e?.target?.value })}
                        placeholder="Enter street address"
                      />
                    ) : (
                      <p className="text-base text-foreground">{formData?.streetAddress || 'N/A'}</p>
                    )}
                  </div>

                  {/* City, State, ZIP */}
                  <div className="grid grid-cols-3 gap-5 mb-5">
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2.5">
                        City
                      </label>
                      {isEditing ? (
                        <Input
                          value={formData?.city}
                          onChange={(e) => setFormData({ ...formData, city: e?.target?.value })}
                          placeholder="City"
                        />
                      ) : (
                        <p className="text-base text-foreground">{formData?.city || 'N/A'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2.5">
                        State
                      </label>
                      {isEditing ? (
                        <Input
                          value={formData?.state}
                          onChange={(e) => setFormData({ ...formData, state: e?.target?.value })}
                          placeholder="State"
                        />
                      ) : (
                        <p className="text-base text-foreground">{formData?.state || 'N/A'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-foreground mb-2.5">
                        ZIP Code
                      </label>
                      {isEditing ? (
                        <Input
                          value={formData?.zipCode}
                          onChange={(e) => setFormData({ ...formData, zipCode: e?.target?.value })}
                          placeholder="ZIP"
                        />
                      ) : (
                        <p className="text-base text-foreground">{formData?.zipCode || 'N/A'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Customer/Personal Details - Show only when contactType = 'customer' */}
                {isCustomerContact && (
                  <div className="border-t border-border pt-6">
                    <h3 className="text-md font-semibold text-foreground mb-4">Personal Details</h3>
                    
                    {/* Household Type */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Household Type
                      </label>
                      {isEditing ? (
                        <select
                          value={formData?.householdType}
                          onChange={(e) => setFormData({ ...formData, householdType: e?.target?.value })}
                          className="w-full h-11 px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 focus:shadow-xl focus:shadow-gray-400/50 bg-background text-foreground font-medium transition-all duration-200 hover:border-gray-400"
                        >
                          <option value="">Select...</option>
                          <option value="single_family">Single Family</option>
                          <option value="condo">Condo</option>
                          <option value="apartment">Apartment</option>
                          <option value="rental">Rental</option>
                          <option value="townhouse">Townhouse</option>
                          <option value="other">Other</option>
                        </select>
                      ) : (
                        <p className="text-base text-foreground capitalize">{formData?.householdType?.replace('_', ' ') || 'N/A'}</p>
                      )}
                    </div>

                    {/* Homeowner or Renter */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Homeowner or Renter
                      </label>
                      {isEditing ? (
                        <select
                          value={formData?.homeownerStatus}
                          onChange={(e) => setFormData({ ...formData, homeownerStatus: e?.target?.value })}
                          className="w-full h-11 px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 focus:shadow-xl focus:shadow-gray-400/50 bg-background text-foreground font-medium transition-all duration-200 hover:border-gray-400"
                        >
                          <option value="">Select...</option>
                          <option value="homeowner">Homeowner</option>
                          <option value="renter">Renter</option>
                        </select>
                      ) : (
                        <p className="text-base text-foreground capitalize">{formData?.homeownerStatus || 'N/A'}</p>
                      )}
                    </div>

                    {/* Spouse/Partner Name */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Spouse/Partner Name
                      </label>
                      {isEditing ? (
                        <Input
                          value={formData?.spousePartnerName}
                          onChange={(e) => setFormData({ ...formData, spousePartnerName: e?.target?.value })}
                          placeholder="Enter spouse/partner name"
                        />
                      ) : (
                        <p className="text-base text-foreground">{formData?.spousePartnerName || 'N/A'}</p>
                      )}
                    </div>

                    <h4 className="text-sm font-semibold text-foreground mb-3 mt-6">Property Details</h4>

                    {/* Square Footage & Year Built */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Square Footage
                        </label>
                        {isEditing ? (
                          <Input
                            type="number"
                            value={formData?.squareFootage}
                            onChange={(e) => setFormData({ ...formData, squareFootage: e?.target?.value })}
                            placeholder="Enter sq ft"
                          />
                        ) : (
                          <p className="text-base text-foreground">{formData?.squareFootage || 'N/A'}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Year Built
                        </label>
                        {isEditing ? (
                          <Input
                            type="number"
                            value={formData?.yearBuilt}
                            onChange={(e) => setFormData({ ...formData, yearBuilt: e?.target?.value })}
                            placeholder="Enter year"
                          />
                        ) : (
                          <p className="text-base text-foreground">{formData?.yearBuilt || 'N/A'}</p>
                        )}
                      </div>
                    </div>

                    {/* Gate Code */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Gate Code / Access Instructions
                      </label>
                      {isEditing ? (
                        <textarea
                          value={formData?.accessInstructions}
                          onChange={(e) => setFormData({ ...formData, accessInstructions: e?.target?.value })}
                          placeholder="Enter access instructions"
                          rows={3}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                        />
                      ) : (
                        <p className="text-base text-foreground">{formData?.accessInstructions || 'N/A'}</p>
                      )}
                    </div>

                    {/* Pets on Property */}
                    <div className="mb-4">
                      <label className="flex items-center space-x-2">
                        {isEditing ? (
                          <input
                            type="checkbox"
                            checked={formData?.hasPets}
                            onChange={(e) => setFormData({ ...formData, hasPets: e?.target?.checked })}
                            className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                          />
                        ) : null}
                        <span className="text-sm font-medium text-foreground">
                          {isEditing ? 'Pets on Property' : `Pets: ${formData?.hasPets ? 'Yes' : 'No'}`}
                        </span>
                      </label>
                      {formData?.hasPets && (
                        <div className="mt-2">
                          {isEditing ? (
                            <Input
                              value={formData?.petNotes}
                              onChange={(e) => setFormData({ ...formData, petNotes: e?.target?.value })}
                              placeholder="Pet details (breed, size, temperament)"
                            />
                          ) : (
                            <p className="text-sm text-muted-foreground">{formData?.petNotes || 'No details'}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Business Details - Show only when contactType = 'business' */}
                {isBusinessContact && (
                  <div className="border-t border-border pt-6">
                    <h3 className="text-md font-semibold text-foreground mb-4">Business Identity</h3>
                    
                    {/* Business Name */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Business Name
                      </label>
                      {isEditing ? (
                        <Input
                          value={formData?.businessName}
                          onChange={(e) => setFormData({ ...formData, businessName: e?.target?.value })}
                          placeholder="Enter business name"
                        />
                      ) : (
                        <p className="text-base text-foreground">{formData?.businessName || 'N/A'}</p>
                      )}
                    </div>

                    {/* Industry */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Industry
                      </label>
                      {isEditing ? (
                        <select
                          value={formData?.industry}
                          onChange={(e) => setFormData({ ...formData, industry: e?.target?.value })}
                          className="w-full h-11 px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 focus:shadow-xl focus:shadow-gray-400/50 bg-background text-foreground font-medium transition-all duration-200 hover:border-gray-400"
                        >
                          <option value="">Select industry...</option>
                          <option value="residential">Residential</option>
                          <option value="commercial">Commercial</option>
                          <option value="industrial">Industrial</option>
                          <option value="government">Government</option>
                          <option value="other">Other</option>
                        </select>
                      ) : (
                        <p className="text-base text-foreground capitalize">{formData?.industry || 'N/A'}</p>
                      )}
                    </div>

                    <h4 className="text-sm font-semibold text-foreground mb-3 mt-6">Business Contact Information</h4>

                    {/* Main Phone */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Main Phone
                      </label>
                      {isEditing ? (
                        <Input
                          value={formData?.mainPhone}
                          onChange={(e) => setFormData({ ...formData, mainPhone: e?.target?.value })}
                          placeholder="Enter main phone"
                        />
                      ) : (
                        <p className="text-base text-foreground">{formData?.mainPhone || 'N/A'}</p>
                      )}
                    </div>

                    {/* Website */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Website
                      </label>
                      {isEditing ? (
                        <Input
                          type="url"
                          value={formData?.website}
                          onChange={(e) => setFormData({ ...formData, website: e?.target?.value })}
                          placeholder="https://example.com"
                        />
                      ) : (
                        <p className="text-base text-foreground">{formData?.website || 'N/A'}</p>
                      )}
                    </div>

                    <h4 className="text-sm font-semibold text-foreground mb-3 mt-6">Relationship Fields</h4>

                    {/* Primary Contact */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Primary Contact (Customer)
                      </label>
                      {isEditing ? (
                        <select
                          value={formData?.primaryContactId}
                          onChange={(e) => setFormData({ ...formData, primaryContactId: e?.target?.value })}
                          className="w-full h-11 px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 focus:shadow-xl focus:shadow-gray-400/50 bg-background text-foreground font-medium transition-all duration-200 hover:border-gray-400"
                        >
                          <option value="">Select primary contact...</option>
                          {customerContacts?.map((c) => (
                            <option key={c?.id} value={c?.id}>
                              {c?.fullName} ({c?.email})
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-base text-foreground">
                          {contact?.primaryContact ? `${contact?.primaryContact?.first_name} ${contact?.primaryContact?.last_name}` : 'Not assigned'}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="border-t border-border pt-6">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Notes
                  </label>
                  {isEditing ? (
                    <textarea
                      value={formData?.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                      placeholder="Add notes about this contact"
                      rows={4}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                    />
                  ) : (
                    <p className="text-base text-foreground">{formData?.notes || 'No notes'}</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'deals' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-foreground">Associated Deals</h4>
                  <Button variant="outline" size="sm">
                    <Icon name="Plus" size={16} className="mr-2" />
                    Add Deal
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">No deals associated with this contact yet.</p>
              </div>
            )}

            {activeTab === 'activities' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-foreground">Recent Activities</h4>
                  <Button variant="outline" size="sm">
                    <Icon name="Plus" size={16} className="mr-2" />
                    Log Activity
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">No activities logged yet.</p>
              </div>
            )}

            {activeTab === 'notes' && (
              <div>
                <div className="mb-4">
                  <h4 className="font-medium text-foreground mb-3">Notes</h4>
                  <div className="space-y-3">
                    <Input
                      placeholder="Add a note about this contact..."
                      value={newNote}
                      onChange={(e) => setNewNote(e?.target?.value)}
                    />
                    <Button 
                      onClick={() => {
                        console.log('Adding note:', newNote);
                        setNewNote('');
                      }}
                      disabled={!newNote?.trim()}
                      size="sm"
                    >
                      <Icon name="Plus" size={16} className="mr-2" />
                      Add Note
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">No notes added yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactDrawer;